import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, RotateCcw, Upload } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { SECTIONS, DEFAULTS } from "@/content/schema";

export default function TextesAdmin() {
  const [values, setValues] = useState({});
  const [active, setActive] = useState(SECTIONS[0].id);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");

  const uploadImage = async (key, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      set(key, data.url);
      toast.success("Photo téléversée — pensez à enregistrer");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading("");
      e.target.value = "";
    }
  };

  useEffect(() => {
    api.get("/content").then((r) => setValues(r.data || {})).catch(() => {});
  }, []);

  const section = SECTIONS.find((s) => s.id === active);
  const val = (key) => (values[key] !== undefined ? values[key] : DEFAULTS[key]);
  const set = (key, v) => setValues((prev) => ({ ...prev, [key]: v }));
  const reset = (key) => setValues((prev) => { const n = { ...prev }; delete n[key]; return n; });

  const save = async () => {
    setSaving(true);
    try {
      const changed = Object.fromEntries(Object.entries(values).filter(([k, v]) => v !== DEFAULTS[k]));
      const { data } = await api.put("/admin/content", changed);
      setValues(data);
      toast.success("Textes enregistrés — le site est à jour");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const input = "mt-1.5 w-full rounded-2xl border border-line bg-cream-light px-4 py-3 text-sm outline-none focus:border-teal";

  return (
    <div data-testid="admin-textes-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl text-teal-dark">Textes du site</h1>
          <p className="text-sm text-ink/60 mt-1">Modifiez chaque texte visible sur le site. Un mot entre *étoiles* s'affiche en italique coloré.</p>
        </div>
        <button data-testid="admin-textes-save" onClick={save} disabled={saving} className="flex items-center gap-2 rounded-full bg-teal px-6 py-2.5 text-sm font-medium text-cream hover:bg-teal-dark transition-colors disabled:opacity-60">
          {saving && <Loader2 size={15} className="animate-spin" />}
          {saving ? "Enregistrement…" : "Enregistrer les textes"}
        </button>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <nav className="lg:col-span-3 flex lg:flex-col gap-1.5 overflow-x-auto pb-2 lg:pb-0">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              data-testid={`admin-textes-tab-${s.id}`}
              onClick={() => setActive(s.id)}
              className={`shrink-0 text-left rounded-2xl px-4 py-2.5 text-sm transition-colors ${active === s.id ? "bg-teal text-cream" : "bg-white border border-line text-ink hover:border-teal"}`}
            >
              {s.label}
            </button>
          ))}
        </nav>

        <div className="lg:col-span-9 bg-white rounded-3xl border border-line p-6 md:p-8 space-y-5">
          <h2 className="font-serif text-2xl text-teal-dark">{section.label}</h2>
          {section.id === "home" && (
            <p data-testid="admin-textes-home-hint" className="rounded-2xl bg-mint/20 px-4 py-3 text-sm text-teal-dark">
              Le surtitre, le grand titre (2 lignes) et le sous-titre du hero se modifient dans <Link to="/admin/reglages" className="underline font-medium">Réglages</Link>, avec le nom de la boutique et le slogan.
            </p>
          )}
          {section.fields.map((f) => {
            const modified = values[f.key] !== undefined && values[f.key] !== DEFAULTS[f.key];
            return (
              <label key={f.key} className="block text-sm font-medium text-ink">
                <span className="flex items-center justify-between gap-3">
                  <span>{f.label}{f.type === "list" && <span className="text-mauve font-normal"> — une ligne par élément</span>}</span>
                  {modified && (
                    <button type="button" data-testid={`admin-textes-reset-${f.key}`} onClick={() => reset(f.key)} className="inline-flex items-center gap-1 text-[11px] font-normal text-mauve hover:text-teal">
                      <RotateCcw size={11} /> texte d'origine
                    </button>
                  )}
                </span>
                {f.type === "image" ? (
                  <div className="mt-2 flex items-center gap-4">
                    <img data-testid={`admin-textes-preview-${f.key}`} src={val(f.key)} alt="" className="h-24 w-24 rounded-2xl object-cover border border-line bg-cream-light" />
                    <label className="inline-flex items-center gap-2 rounded-full border border-line px-4 py-2 text-sm cursor-pointer hover:border-teal">
                      {uploading === f.key ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
                      {uploading === f.key ? "Envoi…" : "Remplacer la photo"}
                      <input type="file" accept="image/*" data-testid={`admin-textes-upload-${f.key}`} onChange={(e) => uploadImage(f.key, e)} className="hidden" />
                    </label>
                  </div>
                ) : f.type === "text" ? (
                  <input data-testid={`admin-textes-field-${f.key}`} value={val(f.key)} onChange={(e) => set(f.key, e.target.value)} className={input} />
                ) : (
                  <textarea data-testid={`admin-textes-field-${f.key}`} rows={f.type === "list" ? Math.max(3, DEFAULTS[f.key].split("\n").length + 1) : 4} value={val(f.key)} onChange={(e) => set(f.key, e.target.value)} className={input} />
                )}
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
}
